import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { useDarkStore } from '../store/darkStore';
import { callOpenAI } from '../../lib/functions';
import { getAvatar } from '../../lib/avatars';

interface ChatMsg {
  id: string;
  fromUser: boolean;
  text: string;
}

const TONE_LABELS: Record<string, string> = {
  cheerful: 'enthusiastic, positive, and encouraging',
  chill: 'relaxed, friendly, and laid-back',
  curious: 'thoughtful, inquisitive, and exploratory',
  coach: 'motivating, direct, and goal-focused',
};

export default function KokoFloat() {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const { explorerName } = useDarkStore();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [history, setHistory] = useState<Array<{ role: 'user' | 'assistant'; text: string }>>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const displayName = profile?.explorerName || explorerName || profile?.displayName || 'Explorer';
  const sidekickName = profile?.sidekickPreferences?.name || 'KoKo';
  const tone = profile?.sidekickPreferences?.tone || 'cheerful';
  const avatar = getAvatar(profile?.sidekickPreferences?.avatarId ?? '');

  const systemPrompt =
    `You are ${sidekickName}, a ${TONE_LABELS[tone] ?? 'friendly'} business mentor for young entrepreneurs. ` +
    `The explorer you are helping is named ${displayName}. ` +
    `Keep every reply to 2-4 sentences, warm, and age-appropriate for kids aged 8-17. ` +
    `Focus on business ideas, creativity, and entrepreneurship. Be their go-to business buddy!`;

  // Scroll to bottom whenever messages change
  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [messages, thinking]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || thinking || !user) return;

    const userMsg: ChatMsg = { id: `u-${Date.now()}`, fromUser: true, text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setThinking(true);
    setError('');

    // Build conversation context for the prompt
    const contextLines = history
      .map((m) => `${m.role === 'user' ? displayName : sidekickName}: ${m.text}`)
      .join('\n');
    const prompt = contextLines
      ? `${contextLines}\n${displayName}: ${text}\n${sidekickName}:`
      : text;

    try {
      const result = await callOpenAI({ prompt, systemPrompt });
      const aiText = result.data.responseText.trim();
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, fromUser: false, text: aiText }]);
      setHistory((prev) => [
        ...prev,
        { role: 'user', text },
        { role: 'assistant', text: aiText },
      ]);
    } catch {
      setError('Something went wrong — give it another try!');
    } finally {
      setThinking(false);
    }
  }, [input, thinking, user, history, displayName, sidekickName, systemPrompt]);

  return (
    <>
      {/* Floating button */}
      <motion.button
        onClick={() => setOpen((p) => !p)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full shadow-lg"
        style={{ background: '#F59E0B', boxShadow: '0 0 20px rgba(245,158,11,0.4)' }}
        aria-label={open ? `Close ${sidekickName}` : `Chat with ${sidekickName}`}
      >
        {!open && (
          <motion.span
            animate={{ scale: [1, 1.45], opacity: [0.5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            className="absolute inset-0 rounded-full"
            style={{ background: '#F59E0B' }}
          />
        )}
        {open ? (
          <span className="relative text-xl font-bold text-black">✕</span>
        ) : (
          <span className="relative flex h-9 w-9 items-center justify-center">
            {avatar.svg()}
          </span>
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(0,0,0,0.5)' }}
              aria-hidden="true"
            />
            <motion.div
              role="dialog"
              aria-label={`Chat with ${sidekickName}`}
              aria-modal="true"
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 z-50 flex max-h-[80vh] flex-col rounded-t-3xl md:inset-x-auto md:bottom-4 md:right-4 md:top-4 md:w-80 md:rounded-2xl"
              style={{ background: '#13162A', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {/* Header */}
              <div
                className="flex shrink-0 items-center justify-between px-4 py-3"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center">{avatar.svg()}</span>
                  <div>
                    <h2 className="font-marker text-sm text-white">{sidekickName}</h2>
                    <p className="font-gill text-[10px]" style={{ color: 'rgba(232,234,240,0.4)' }}>
                      Your business sidekick
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full font-gill text-sm transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(232,234,240,0.5)' }}
                  aria-label={`Close ${sidekickName}`}
                >
                  ✕
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" aria-live="polite">
                {messages.length === 0 && (
                  <div className="pt-6 text-center">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center">{avatar.svg()}</span>
                    <p className="mt-3 font-marker text-sm text-white">
                      Hey{displayName ? `, ${displayName}` : ''}!
                    </p>
                    <p className="mt-1 font-gill text-xs" style={{ color: 'rgba(232,234,240,0.45)' }}>
                      Got a question about your business? Ask away!
                    </p>
                  </div>
                )}

                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.fromUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="max-w-[85%] rounded-2xl px-3 py-2 font-gill text-sm leading-relaxed"
                      style={
                        msg.fromUser
                          ? { background: '#F59E0B', color: '#000' }
                          : { background: 'rgba(255,255,255,0.07)', color: 'rgba(232,234,240,0.9)' }
                      }
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}

                {/* Thinking indicator */}
                {thinking && (
                  <div className="flex justify-start">
                    <div
                      className="flex items-center gap-1 rounded-2xl px-3 py-2"
                      style={{ background: 'rgba(255,255,255,0.07)' }}
                    >
                      {[0, 150, 300].map((d) => (
                        <span
                          key={d}
                          className="koko-dot inline-block h-1.5 w-1.5 rounded-full"
                          style={{ background: 'rgba(232,234,240,0.5)', animationDelay: `${d}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {error && (
                  <p className="text-center font-gill text-xs" style={{ color: '#F87171' }}>{error}</p>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div
                className="shrink-0 px-4 py-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setError(''); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    disabled={thinking}
                    placeholder={`Ask ${sidekickName} anything…`}
                    rows={1}
                    className="max-h-20 min-h-[40px] flex-1 resize-none rounded-xl px-3 py-2 font-gill text-sm outline-none transition-colors disabled:opacity-40"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(232,234,240,0.9)',
                    }}
                    aria-label={`Message to ${sidekickName}`}
                  />
                  <button
                    onClick={send}
                    disabled={thinking || !input.trim()}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all disabled:opacity-30"
                    style={{ background: '#F59E0B' }}
                    aria-label="Send"
                  >
                    <svg className="h-4 w-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
